import { useMemo, useState } from 'react'
import { Camera, Check, Loader2, Map as MapIcon, Palette, Printer } from 'lucide-react'

import { useAuth } from '../../auth/authContext'
import { useRoadbook } from '../../data/roadbookContext'
import { today } from '../../lib/format'
import { activeStages } from '../../lib/trip'
import { hasWebgl } from '../map/basemap'
import { PrintBook } from './PrintBook'
import { usePrintMaps, usePrintPhotos } from './usePrintAssets'

/**
 * Der Druckbereich: Vorbereitung links, Vorschau darunter.
 *
 * Warum es diesen Bereich überhaupt gibt, statt dass der Knopf in der
 * Reiterleiste einfach window.print() ruft: der häufigste Grund, warum ein am
 * Bildschirm schönes Layout auf Papier scheitert, ist eine Einstellung im
 * Druckdialog, die kein CSS erzwingen kann. Hintergrundfarben druckt der
 * Browser nur, wenn "Hintergrundgrafiken" angehakt ist - print-color-adjust
 * sagt lediglich, dass er die Farben nicht aufhellen soll, nicht dass er sie
 * überhaupt drucken soll. Ohne den Haken kommen die dunklen Bänder der
 * Titelseite und die Kopfzonen der Etappen weiss heraus, und das Buch sieht
 * aus, als wäre die Hälfte vergessen worden.
 *
 * Dazu kommt der Kartenexport, der Zeit braucht und bei dem man zusehen
 * können muss. Beides gehört an einen Ort, der vor dem Druckdialog liegt.
 */
export function PrintView() {
  const { trip, diary, postsByStage, expenses, checks, photosByStage } = useRoadbook()
  const { config } = useAuth()

  const stages = useMemo(() => activeStages(trip), [trip])
  const alleFotos = useMemo(
    () => stages.flatMap((stage) => photosByStage[stage.id] ?? []),
    [stages, photosByStage],
  )

  const fotos = usePrintPhotos(alleFotos)
  const karten = usePrintMaps(stages, diary)

  // Das Druckdatum steht auf der Rückseite und darf sich nicht bei jedem
  // Rendern ändern.
  const [gedrucktAm] = useState(today)
  const [webgl] = useState(hasWebgl)

  const hatArchiv = config?.hasBasemap ?? false
  const kartenMoeglich = webgl && hatArchiv

  if (stages.length === 0) {
    return (
      <section className="seite">
        <p className="leer">Ohne Etappen gibt es nichts zu drucken.</p>
      </section>
    )
  }

  return (
    <section className="seite dr-bereich">
      <div className="dr-vorbereitung kein-druck">
        <h2>Vor dem Drucken</h2>
        <p className="dr-vorbereitung-text">
          Das Buch ist auf A5 hoch gesetzt, eine Etappe je Seite, mit Kartenausschnitt und Fotos.
          Drei Handgriffe, dann stimmt es auch auf Papier.
        </p>

        <ol className="dr-schritte">
          <li>
            <span className="dr-schritt-nummer">
              <MapIcon size={15} />
            </span>
            <div>
              <h3>Kartenausschnitte rendern</h3>
              <p>
                {kartenMoeglich
                  ? 'Je Etappe ein Bild aus der echten Karte, dazu die Übersichtskarte. ' +
                    'Ohne diesen Schritt stehen im Buch Skizzen statt Karten.'
                  : !webgl
                    ? 'Dieser Browser kann kein WebGL. Das Buch nimmt die Skizzen — sie ' +
                      'drucken sicher, zeigen aber keine Strassen und keine Orte.'
                    : 'Auf dem Server liegt kein Kartenarchiv. Das Buch nimmt die Skizzen.'}
              </p>

              {karten.phase === 'laeuft' && (
                <p className="dr-fortschritt">
                  <Loader2 size={13} className="dreht" /> {karten.done} von {karten.total} Karten
                </p>
              )}
              {karten.phase === 'fertig' && (
                <p className="dr-fortschritt dr-gut">
                  <Check size={13} /> {Object.keys(karten.urls).length} Karten fertig
                  {karten.missing > 0 && ` · ${karten.missing} als Skizze`}
                </p>
              )}
              {karten.phase === 'fehler' && (
                <p className="dr-fortschritt dr-schlecht">
                  Es kam kein einziger Ausschnitt zustande. Das Buch nimmt die Skizzen.
                </p>
              )}

              {kartenMoeglich && karten.phase !== 'laeuft' && (
                <button
                  type="button"
                  className="knopf"
                  onClick={karten.start}
                  disabled={!fotos.ready && fotos.total > 0}
                >
                  <MapIcon size={14} />{' '}
                  {karten.phase === 'leer' ? 'Karten rendern' : 'Noch einmal rendern'}
                </button>
              )}
            </div>
          </li>

          <li>
            <span className="dr-schritt-nummer">
              <Camera size={15} />
            </span>
            <div>
              <h3>Fotos laden</h3>
              <p>
                Alle Bilder müssen vor dem Druckdialog geladen sein — was erst währenddessen käme,
                fehlt auf dem Papier.
              </p>
              {fotos.total === 0 ? (
                <p className="dr-fortschritt">Keine Fotos erfasst.</p>
              ) : fotos.ready ? (
                <p className="dr-fortschritt dr-gut">
                  <Check size={13} /> {Object.keys(fotos.urls).length} von {fotos.total} Fotos
                  bereit
                </p>
              ) : (
                <p className="dr-fortschritt">
                  <Loader2 size={13} className="dreht" /> {fotos.loaded} von {fotos.total}
                </p>
              )}
            </div>
          </li>

          <li>
            <span className="dr-schritt-nummer">
              <Palette size={15} />
            </span>
            <div>
              <h3>Im Druckdialog: Hintergrundgrafiken einschalten</h3>
              {/*
                Der wichtigste Absatz dieses Bereichs. Kein CSS kann diese
                Einstellung erzwingen - wer sie vergisst, bekommt ein Buch ohne
                die dunklen Bänder und ohne die Kopfzonen.
              */}
              <p>
                Ohne diesen Haken bleiben alle farbigen Flächen weiss. In Chrome und Edge steht er
                unter <b>Weitere Einstellungen</b> → <b>Hintergrundgrafiken</b>, in Firefox unter{' '}
                <b>Weitere Einstellungen</b> → <b>Hintergrund drucken</b>, in Safari unter{' '}
                <b>Hintergrund drucken</b>.
              </p>
              <p>
                Ebenfalls dort: Papierformat <b>A5</b>, Ränder <b>keine</b>, Skalierung <b>100 %</b>
                . Das Buch bringt seine Ränder selbst mit.
              </p>
            </div>
          </li>
        </ol>

        <div className="knopfreihe">
          <button type="button" className="knopf" onClick={() => window.print()}>
            <Printer size={15} /> Drucken
          </button>
          {!fotos.ready && fotos.total > 0 && (
            <span className="dr-warten">Die Fotos sind noch nicht alle da.</span>
          )}
        </div>
      </div>

      {/*
        Die Vorschau ist dasselbe Markup wie der Druck, nur schmaler: die Seite
        rechnet ihre gesamte Innengeometrie aus ihrer eigenen Breite, siehe
        print.css. Was hier steht, kommt so aufs Papier - bis auf die
        Hintergrundfarben, und genau davon handelt der Hinweis oben.
      */}
      <div className="dr-vorschau">
        <PrintBook
          trip={trip}
          diary={diary}
          postsByStage={postsByStage}
          expenses={expenses}
          checks={checks}
          photosByStage={photosByStage}
          photoUrls={fotos.urls}
          mapUrls={karten.urls}
          gedrucktAm={gedrucktAm}
        />
      </div>
    </section>
  )
}
